import {useEffect} from 'react';
import {Capacitor} from '@capacitor/core';

const SERVER = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
const ABS = /^(https?:|data:|blob:|file:|capacitor:)/i;
const MEDIA_SEL = 'img, audio, video, source, track';
const LAZY_ATTRS = [
  'data-src','data-srcset','data-bg','data-background','data-bgset',
  'data-lazy','data-lazy-src','data-lazy-srcset','data-original','data-original-src',
  'poster','data-poster'
];
const MARK = 'data-absproxied';

const toAbs = (u?: string|null) =>
  !u || ABS.test(u) || !SERVER ? (u ?? '') : `${SERVER}/${u.replace(/^\/+/, '')}`;

const rewriteSrcset = (val: string) =>
  val.split(',').map(p=>{
    const [u,s] = p.trim().split(/\s+/,2);
    const u2 = toAbs(u) || u;
    return s ? `${u2} ${s}` : u2;
  }).join(', ');

function absolutizeMedia(el: Element) {
  const src = el.getAttribute('src');
  const next = toAbs(src);
  if (src && next && next !== src) el.setAttribute('src', next);

  const set = el.getAttribute('srcset');
  if (set) {
    const fixed = rewriteSrcset(set);
    if (fixed !== set) el.setAttribute('srcset', fixed);
  }
  (el as HTMLElement).setAttribute(MARK, '1');
}

function absolutizeLazyAttrs(el: Element) {
  for (const a of LAZY_ATTRS) {
    const v = el.getAttribute(a);
    if (!v) continue;
    if (a.endsWith('srcset')) {
      const fixed = rewriteSrcset(v);
      if (fixed !== v) el.setAttribute(a, fixed);
    } else {
      const fixed = toAbs(v);
      if (fixed && fixed !== v) el.setAttribute(a, fixed);
    }
  }
}

function absolutizeBackground(el: Element) {
  const cs = getComputedStyle(el as HTMLElement);
  const bg = cs.backgroundImage;
  if (!bg || !bg.includes('url(')) return;
  const fixed = bg.replace(/url\(([^)]+)\)/g, (_m, raw) => {
    let url = String(raw).trim().replace(/^['"]|['"]$/g, '');
    if (/^https?:/i.test(url)) return `url("${url}")`;
    if (url.startsWith(window.location.origin)) {
      url = url.slice(window.location.origin.length);
    }
    return `url("${toAbs(url)}")`;
  });
  if (fixed !== bg) (el as HTMLElement).style.backgroundImage = fixed;
}

export function useDomAssetProxy() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !SERVER) return;

    // initial pass
    document.querySelectorAll(MEDIA_SEL).forEach(el => {
      absolutizeLazyAttrs(el);
      absolutizeMedia(el);
    });
    document.querySelectorAll<HTMLElement>('*').forEach(el => {
      absolutizeLazyAttrs(el);
      absolutizeBackground(el);
    });

    // lazysizes hook (si utilisé)
    const onLazy = (e: Event) => {
      const el = e.target as Element;
      absolutizeLazyAttrs(el);
      absolutizeMedia(el);
      absolutizeBackground(el);
    };
    document.addEventListener('lazybeforeunveil', onLazy, true);

    // mutations
    const obs = new MutationObserver(muts => {
      for (const m of muts) {
        if (m.type === 'childList') {
          m.addedNodes.forEach(n => {
            if (!(n instanceof Element)) return;
            absolutizeLazyAttrs(n); absolutizeBackground(n);
            if (n.matches(MEDIA_SEL)) absolutizeMedia(n);
            n.querySelectorAll?.(MEDIA_SEL).forEach(el=>{
              absolutizeLazyAttrs(el); absolutizeMedia(el);
            });
            n.querySelectorAll?.<HTMLElement>('*').forEach(el=>{
              absolutizeLazyAttrs(el); absolutizeBackground(el);
            });
          });
        } else if (m.type === 'attributes' && m.target instanceof Element) {
          const t = m.target;
          if (m.attributeName && LAZY_ATTRS.includes(m.attributeName)) {
            absolutizeLazyAttrs(t);
          }
          if (t.matches(MEDIA_SEL) && (m.attributeName === 'src' || m.attributeName === 'srcset')) {
            absolutizeMedia(t);
          }
          if (m.attributeName === 'style' || m.attributeName === 'class') {
            absolutizeBackground(t);
          }
        }
      }
    });
    obs.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['src','srcset','style','class', ...LAZY_ATTRS],
    });

    return () => {
      document.removeEventListener('lazybeforeunveil', onLazy, true);
      obs.disconnect();
    };
  }, []);
}
